import { CompactInput, PropertiesPanelControl, PropertiesPanelLabel, PropertiesPanelProperty } from './propertiesPanelProperty';
import React from 'react';
import styled from 'styled-components';
import { useIsSceneLook } from './propertiesPanelLook';
import { PropertiesPanelNumberField } from './propertiesPanelNumberField';
import { PropertiesPanelAxisLetter, PropertiesPanelAxisToggle } from './propertiesPanelAxisTogglesProperty';

const RangeInput = styled.input`
    flex: 1;
    min-width: 0;
`;

const NumberInput = styled(CompactInput)`
    width: 90px;
    flex-shrink: 0;
    flex: unset;
    text-align: center;
`;

const SceneControl = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    min-width: 0;
`;

// LA LETTERA STA IN TESTA AL CONTROLLO: le lettere di righe diverse cadono alla stessa x — subito dopo il nome — e
// il campo la segue fino in fondo alla riga.
//
// SPENTA DA SOLA, SI SPEGNE LEI: la riga resta viva — una ruvidità vale anche senza la mappa da cui
// leggerla — e il velo tocca solo la lettera. Quando è spenta la riga intera, il velo ce l'ha già la
// riga, e un secondo lo moltiplicherebbe.
const Tag = styled.span<{ $off: boolean }>`
    display: inline-flex;
    flex: none;
    opacity: ${(props) => (props.$off ? 0.5 : 1)};
`;

export type PropertiesPanelRangePropertyProps = {
    id: string;
    label: React.ReactNode;
    value: number;
    min?: number;
    max?: number;
    step?: number;
    /** Il suffisso dentro il campo: «°». Solo nel vestito della scena. */
    unit?: string;
    /**
     * IL CURSORE SI FERMA PRIMA DEL CAMPO. Alcune misure hanno un intervallo utile stretto e
     * nessun tetto vero — lo spessore di un materiale si regola quasi sempre fra 0 e 10, ma
     * ogni tanto serve 40. Con un solo `max` si sceglie fra un cursore inservibile, perché
     * copre un intervallo che non si usa mai, e un valore che non si può scrivere.
     *
     * Quando c'è, il cursore arriva fin qui e il campo numerico resta senza tetto.
     */
    sliderMax?: number;
    /**
     * LA LETTERA DEL CANALE DA CUI IL VALORE LEGGE — la R, la G o la B di una mappa ORM: il patto del
     * pacchetto detto riga per riga, invece che nel solo nome del canale. Solo nel vestito della scena.
     */
    tag?: PropertiesPanelAxisToggle;
    /** L'evento c'è solo quando il valore arriva da un campo: dallo stepper non c'è nessun evento da passare. */
    onChange?: (newValue: number, e?: React.ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
};

export const PropertiesPanelRangeProperty: React.FC<PropertiesPanelRangePropertyProps> = ({
    id,
    label,
    value,
    min,
    max,
    step,
    unit,
    sliderMax,
    tag,
    onChange,
    disabled
}) => {
    const scene = useIsSceneLook();
    const top = sliderMax ?? max;

    if (scene) {
        // UN CAMPO SOLO CHE È ANCHE LO SLIDER (prototipo; utente, 2026-10-20): si riempie fino al valore, si
        // trascina per percorrerlo, si clicca per scrivere. Lo slider accanto al numero era la stessa cosa due volte.
        const low = min ?? 0;
        const high = top ?? 100;

        return (
            <PropertiesPanelProperty disabled={disabled}>
                <PropertiesPanelLabel id={`${id}-label`} htmlFor={id}>
                    {label}
                </PropertiesPanelLabel>
                <SceneControl>
                    {tag && (
                        <Tag $off={Boolean(tag.disabled) && !disabled}>
                            <PropertiesPanelAxisLetter toggle={tag} />
                        </Tag>
                    )}
                    <PropertiesPanelNumberField
                        id={id}
                        value={value}
                        min={min}
                        max={sliderMax === undefined ? max : undefined}
                        step={step}
                        unit={unit}
                        full
                        range={{ min: low, max: high }}
                        disabled={disabled}
                        onChange={(next) => onChange?.(next)}
                    />
                </SceneControl>
            </PropertiesPanelProperty>
        );
    }

    return (
        <PropertiesPanelProperty disabled={disabled}>
            <PropertiesPanelLabel htmlFor={`${id}-input`}>{label}</PropertiesPanelLabel>
            <PropertiesPanelControl>
                <RangeInput
                    id={`${id}-range`}
                    type='range'
                    min={min}
                    max={top}
                    step={step}
                    value={value}
                    disabled={disabled}
                    onChange={(e) => {
                        if (!isNaN(e.target.valueAsNumber)) {
                            onChange?.(e.target.valueAsNumber, e);
                        }
                    }}
                />
                <NumberInput
                    id={id}
                    type={'number'}
                    min={min}
                    max={sliderMax === undefined ? max : undefined}
                    step={step}
                    value={value}
                    disabled={disabled}
                    onChange={(e) => {
                        if (!isNaN(e.target.valueAsNumber)) {
                            onChange?.(e.target.valueAsNumber, e);
                        }
                    }}
                />
            </PropertiesPanelControl>
        </PropertiesPanelProperty>
    );
};
